import { supabase, SUPABASE_URL } from '../supabaseClient';

const BUCKET = 'slide-images';
const CACHE_CONTROL = '31536000';

/**
 * 이미지 저장 용량 한도의 예전 고정값(100MB). 실제 한도는 서버(get_user_image_storage_usage.bytes_limit)가
 * 이용권별로 돌려주며, 이 값은 서버가 한도를 주지 않을 때(구버전 RPC·조회 실패)의 대체값이다.
 */
export const SLIDE_IMAGE_STORAGE_LIMIT_BYTES = 104857600;
/** 이용권별 기본 한도(platform_plan_permissions.storage_limit_bytes 기본값과 동일) */
export const FREE_IMAGE_STORAGE_LIMIT_BYTES = 10485760;
export const PASS_IMAGE_STORAGE_LIMIT_BYTES = 104857600;

export interface SlideImageStorageUsage {
  bytesUsed: number;
  bytesLimit: number;
}

function genStorageFileName(mimeType: string): string {
  const ext = mimeType === 'image/jpeg' ? 'jpg' : 'webp';
  const id = globalThis.crypto?.randomUUID?.();
  if (id) return `${id}.${ext}`;
  return `${Date.now()}-${Math.random().toString(36).slice(2)}.${ext}`;
}

/** public URL에서 storage object path 추출 (본인 slide-images만) */
export function isSlideImageStoragePublicUrl(publicUrl: string): boolean {
  if (!publicUrl.trim() || !SUPABASE_URL) return false;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  return publicUrl.trim().startsWith(prefix);
}

export function storagePathFromSlideImagePublicUrl(publicUrl: string, userId: string): string | null {
  if (!publicUrl.trim() || !SUPABASE_URL) return null;
  const prefix = `${SUPABASE_URL}/storage/v1/object/public/${BUCKET}/`;
  if (!publicUrl.startsWith(prefix)) return null;
  const path = decodeURIComponent(publicUrl.slice(prefix.length).split('?')[0] ?? '');
  if (!path.startsWith(`${userId}/`)) return null;
  return path;
}

export async function fetchSlideImageStorageUsage(): Promise<SlideImageStorageUsage | null> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userData.user) return null;
  const { data, error } = await supabase.rpc('get_user_image_storage_usage');
  if (error) {
    const legacy = await supabase.rpc('get_slide_image_storage_usage');
    if (legacy.error) throw legacy.error;
    const row = Array.isArray(legacy.data) ? legacy.data[0] : legacy.data;
    const bytesUsed = Number((row as { bytes_used?: number })?.bytes_used ?? 0);
    const bytesLimit = Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES);
    return { bytesUsed, bytesLimit };
  }
  const row = Array.isArray(data) ? data[0] : data;
  const bytesUsed = Number((row as { bytes_used?: number })?.bytes_used ?? 0);
  const bytesLimit = Number((row as { bytes_limit?: number })?.bytes_limit ?? SLIDE_IMAGE_STORAGE_LIMIT_BYTES);
  return { bytesUsed, bytesLimit };
}

export async function uploadSlideImage(
  blob: Blob,
  mimeType: string
): Promise<{ publicUrl: string; storagePath: string }> {
  const { data: userData, error: userErr } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (userErr || !userId) throw new Error('로그인이 필요합니다.');

  const usage = await fetchSlideImageStorageUsage();
  if (usage && usage.bytesUsed + blob.size > usage.bytesLimit) {
    throw new Error('QUOTA_EXCEEDED');
  }

  const fileName = genStorageFileName(mimeType);
  const storagePath = `${userId}/${fileName}`;

  const { error: uploadErr } = await supabase.storage.from(BUCKET).upload(storagePath, blob, {
    contentType: mimeType,
    cacheControl: CACHE_CONTROL,
    upsert: false,
  });
  if (uploadErr) throw uploadErr;

  try {
    const { error: regErr } = await supabase.rpc('register_slide_image_asset', {
      p_path: storagePath,
      p_bytes: blob.size,
    });
    if (regErr) {
      await supabase.storage.from(BUCKET).remove([storagePath]);
      if (String(regErr.message ?? '').includes('QUOTA_EXCEEDED')) {
        throw new Error('QUOTA_EXCEEDED');
      }
      throw regErr;
    }
  } catch (e) {
    await supabase.storage.from(BUCKET).remove([storagePath]);
    throw e;
  }

  const { data: urlData } = supabase.storage.from(BUCKET).getPublicUrl(storagePath);
  const publicUrl = urlData.publicUrl;
  if (!publicUrl) throw new Error('이미지 URL을 만들지 못했습니다.');

  notifySlideImageStorageChanged();
  return { publicUrl, storagePath };
}

export async function deleteSlideImageByPublicUrl(publicUrl: string): Promise<void> {
  const { data: userData } = await supabase.auth.getUser();
  const userId = userData.user?.id?.trim();
  if (!userId || !publicUrl.trim()) return;
  const path = storagePathFromSlideImagePublicUrl(publicUrl, userId);
  if (!path) return;
  await supabase.rpc('release_slide_image_asset', { p_path: path });
  await supabase.storage.from(BUCKET).remove([path]);
  notifySlideImageStorageChanged();
}

export function formatSlideStorageMb(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB';
  const mb = bytes / (1024 * 1024);
  if (mb < 1) {
    const kb = bytes / 1024;
    if (kb < 1) return '<1 KB';
    return kb < 10 ? `${kb.toFixed(1)} KB` : `${Math.round(kb)} KB`;
  }
  if (mb < 10) return `${mb.toFixed(1)} MB`;
  return `${Math.round(mb)} MB`;
}

export function slideStorageUsagePercent(bytesUsed: number, bytesLimit: number): number {
  if (!bytesLimit || !Number.isFinite(bytesLimit)) return 0;
  return Math.min(100, Math.round((bytesUsed / bytesLimit) * 100));
}

export function formatSlideStorageUsageLabel(usage: SlideImageStorageUsage): string {
  const pct = slideStorageUsagePercent(usage.bytesUsed, usage.bytesLimit);
  return `${formatSlideStorageMb(usage.bytesUsed)} / ${formatSlideStorageMb(usage.bytesLimit)} (${pct}%)`;
}

export const SLIDE_IMAGE_STORAGE_CHANGED_EVENT = 'cp-slide-image-storage-changed';
const SLIDE_IMAGE_STORAGE_DIRTY_KEY = 'cp-slide-image-storage-dirty';

/** 슬라이드 탭 저장 등 — MainLayout이 없을 때 돌아온 뒤 1회만 갱신하도록 표시 */
export function markSlideImageStorageDirty(): void {
  try {
    sessionStorage.setItem(SLIDE_IMAGE_STORAGE_DIRTY_KEY, '1');
  } catch {
    /* ignore */
  }
}

export function consumeSlideImageStorageDirty(): boolean {
  try {
    if (sessionStorage.getItem(SLIDE_IMAGE_STORAGE_DIRTY_KEY) !== '1') return false;
    sessionStorage.removeItem(SLIDE_IMAGE_STORAGE_DIRTY_KEY);
    return true;
  } catch {
    return false;
  }
}

export function notifySlideImageStorageChanged(): void {
  markSlideImageStorageDirty();
  window.dispatchEvent(new CustomEvent(SLIDE_IMAGE_STORAGE_CHANGED_EVENT));
}
